import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/Button';
import { Input, Label } from '@/components/Input';
import { AuthShell, friendly } from './SignIn';

export function SignUp() {
  const { signUpEmail, signInGoogle } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await signUpEmail(email, password);
      // Email verification is sent automatically by signUpEmail; route to the
      // verification screen.
      nav('/verify-email');
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setErr(null);
    setBusy(true);
    try {
      await signInGoogle();
      nav('/');
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Create your account — 25 free questions to start.">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="password">Password (≥ 6 characters)</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            minLength={6}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {err && <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>}
        <Button type="submit" size="lg" loading={busy} className="w-full">
          Create account
        </Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-stone-500">
        <span className="flex-1 h-px bg-stone-200 dark:bg-stone-800" />
        <span>or</span>
        <span className="flex-1 h-px bg-stone-200 dark:bg-stone-800" />
      </div>
      <Button variant="secondary" size="lg" onClick={onGoogle} loading={busy} className="w-full">
        Continue with Google
      </Button>
      <p className="mt-6 text-center text-sm text-stone-600 dark:text-stone-400">
        Already have an account?{' '}
        <Link to="/signin" className="text-brand-700 dark:text-brand-400 font-medium">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
